/**
 * Bridge for a NATIVE "Paste Plain Text" action, used by the iOS edit-menu item (see
 * src-tauri/ios-overlay/LapoPastePlain.swift). A WKWebView can't read the clipboard from JS without the
 * system "Paste" prompt, so the native side reads `UIPasteboard` itself and hands the string to JS here
 * via `window.__lapoInsertPlainText`. This module tracks whichever editor view currently holds focus so
 * that string lands at the right caret, and inserts it verbatim through the same plain-text path as the
 * desktop Ctrl/Cmd-Shift-V chord (`plainTextToSlice`) — so `>`, `*`, `#`, `[](…)` stay literal.
 */
import type { EditorView } from '@tiptap/pm/view';
import { plainTextToSlice } from './markdownPaste.js';

interface ActiveTarget {
  view: EditorView;
  /** Journal/outline view wraps pasted lines into bullets; a document takes them as paragraphs. */
  outline: boolean;
}

let active: ActiveTarget | null = null;

/** Register the editor view that currently holds focus, so a native menu action can target it. */
export function setActivePlainPasteView(view: EditorView, outline: boolean): void {
  active = { view, outline };
}

/** Clear the active view on blur/destroy — but only if it's still the one we hold (avoid clobbering a
 * newer editor that focused between this view's blur and its teardown). */
export function clearActivePlainPasteView(view: EditorView): void {
  if (active?.view === view) active = null;
}

/**
 * Insert text verbatim (no markdown parsing) at the focused editor's selection — the shared target of
 * the iOS "Paste Plain" edit-menu item. Returns false when no editor is focused, the view is gone, or
 * the text is empty, so the native side can fall back to the OS paste.
 */
export function insertPlainTextIntoActiveEditor(text: string): boolean {
  if (!active || active.view.isDestroyed || !text) return false;
  const { view, outline } = active;
  const slice = plainTextToSlice(text, view.state.schema, { outline });
  if (slice.content.size === 0) return false;
  view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
  view.focus();
  return true;
}

/** The window hook the native iOS shell calls; kept off the typed public surface (it's a bridge). */
interface PlainPasteBridgeWindow {
  __lapoInsertPlainText?: (text: string) => boolean;
}

/**
 * Install the `window.__lapoInsertPlainText` bridge the iOS "Paste Plain" menu item calls with the
 * pasteboard string. Idempotent; call once at startup on iOS. No-op where there's no window (SSR/tests).
 */
export function installNativePlainPasteBridge(): void {
  if (typeof window === 'undefined') return;
  (window as PlainPasteBridgeWindow & Window).__lapoInsertPlainText = (text: string) =>
    insertPlainTextIntoActiveEditor(text);
}
